import type { WidgetProductId } from "./widget-product-id";

/**
 * Canonical widget product identifiers shared by widget-ui, tooling, and widget-ui-infra.
 */
export {
  DEFAULT_WIDGET_TYPE,
  isWidgetProductId,
  WIDGET_DISPLAY_LABEL,
  WIDGET_TYPES,
  type WidgetProductId,
} from "./widget-product-id";

export {
  getWidgetEmbedLandingPath,
  getWidgetRouteEntry,
  resolveWidgetProductIdFromPathname,
  WIDGET_ROUTE_ENTRIES,
  type WidgetRouteEntry,
} from "./widget-routing";

/** Query key for harness/dev: selects demo customer config when `CUSTOMER_CONFIG` is unset. */
export const CONFIG_WIDGET_TYPE_QUERY_PARAM = "widgetType" as const;

/** Base URL segment for hosted JSON Schemas (`/schemas/...`). */
export const SCHEMA_HOST = "https://widget.blockdaemon.com";

/** Namespaced schema URL (`/schemas/{widgetType}/{version}.json`). */
export function namespacedSchemaUrl(widgetType: WidgetProductId, versionSegment: string): string {
  return `${SCHEMA_HOST}/schemas/${widgetType}/${versionSegment}.json`;
}

/** Customer-facing schema URL for `$schema` in config (same path as {@link namespacedSchemaUrl}). */
export function primarySchemaUrl(widgetType: WidgetProductId, versionSegment: string): string {
  return namespacedSchemaUrl(widgetType, versionSegment);
}

/** KV lookup key segments (no `.json`; worker maps path to this key). */
export function kvSchemaKey(widgetType: WidgetProductId, versionSegment: string): string {
  return `${widgetType}/${versionSegment}`;
}

/** R2 prefix for shipped static assets + worker bundle. */
export function r2ArtifactPrefix(widgetType: WidgetProductId): string {
  return `widgets/${widgetType}/releases`;
}

export function r2ArtifactBasePath(widgetType: WidgetProductId, version: string): string {
  const prefix = r2ArtifactPrefix(widgetType);
  return `widget-artifacts/${prefix}/${version}`;
}

export {
  PLATFORM_CURRENCIES,
  PLATFORM_CURRENCY_LABELS,
  PLATFORM_LOCALE_LABELS,
  PLATFORM_LOCALE_MENU_LABELS,
  PLATFORM_LOCALES,
  coerceCurrencyForProduct,
  coerceLocaleForProduct,
  getCurrencySelectOptions,
  getLocaleSelectOptions,
  getSupportedCurrencies,
  getSupportedLocales,
  isPlatformCurrency,
  isPlatformLocale,
  isSupportedCurrency,
  isSupportedLocale,
  type PlatformCurrency,
  type PlatformLocale,
} from "./locale-currency";
